import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  CreditCard,
  Download,
  GraduationCap,
  Heart,
  KeyRound,
  LayoutDashboard,
  LifeBuoy,
  LogOut,
  RefreshCw,
  ShieldCheck,
  ShoppingBag,
  UserCog,
  Sparkles,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import NotificationsBell from "@/components/NotificationsBell";

const MENU = [
  { to: "/compte", label: "Tableau de bord", icon: LayoutDashboard, end: true, testId: "account-nav-dashboard" },
  { to: "/compte/commandes", label: "Mes commandes", icon: ShoppingBag, testId: "account-nav-orders" },
  { to: "/compte/licences", label: "Mes licences", icon: KeyRound, testId: "account-nav-licenses" },
  { to: "/compte/formations", label: "Mes formations", icon: GraduationCap, testId: "account-nav-formations" },
  { to: "/compte/telechargements", label: "Mes téléchargements", icon: Download, testId: "account-nav-downloads" },
  { to: "/compte/factures", label: "Mes factures", icon: CreditCard, testId: "account-nav-invoices" },
  { to: "/compte/favoris", label: "Mes favoris", icon: Heart, testId: "account-nav-favorites" },
  { to: "/compte/abonnements", label: "Mes abonnements", icon: RefreshCw, testId: "account-nav-subscriptions" },
  { to: "/compte/tickets", label: "Mes tickets support", icon: LifeBuoy, testId: "account-nav-tickets" },
  { to: "/compte/activite", label: "Mon activité (IA)", icon: Sparkles, testId: "account-nav-activity" },
  { to: "/compte/profil", label: "Informations personnelles", icon: UserCog, testId: "account-nav-profile" },
  { to: "/compte/securite", label: "Sécurité", icon: ShieldCheck, testId: "account-nav-security" },
];

export function RequireAuth({ children, adminOnly = false }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-brand" />
      </div>
    );
  }
  if (!user) return <Navigate to="/connexion" replace state={{ from: window.location.pathname }} />;
  if (adminOnly && user.role !== "admin")
    return <Navigate to={user.role === "vendor" ? "/vendeur/dashboard" : "/compte"} replace />;
  return children;
}

export default function AccountLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="container-it py-6 lg:py-10">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="label-eyebrow mb-1.5">Espace client</p>
          <h1 className="font-display text-2xl font-extrabold text-ink sm:text-3xl" data-testid="account-greeting">
            Bonjour {user?.name?.split(" ")[0]} 👋
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <NotificationsBell />
          <span className="rounded-full border border-line bg-white px-4 py-2 text-xs font-semibold text-slate-500">
            {user?.email}
          </span>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[260px_1fr] lg:gap-8">
        <aside className="min-w-0">
          <nav className="flex gap-2 overflow-x-auto no-scrollbar lg:sticky lg:top-[150px] lg:flex-col lg:gap-1 lg:overflow-visible">
            {MENU.map(({ to, label, icon: Icon, end, testId }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                data-testid={testId}
                className={({ isActive }) =>
                  `flex shrink-0 items-center gap-2.5 whitespace-nowrap rounded-xl px-4 py-3 text-sm font-semibold transition-colors duration-200 ${
                    isActive ? "bg-brand text-white" : "border border-line bg-white text-ink hover:border-brand lg:border-transparent lg:hover:bg-brand-soft"
                  }`
                }
              >
                <Icon className="h-4 w-4" />
                {label}
              </NavLink>
            ))}
            <button
              data-testid="account-logout"
              onClick={async () => {
                await logout();
                navigate("/");
              }}
              className="flex shrink-0 items-center gap-2.5 whitespace-nowrap rounded-xl px-4 py-3 text-sm font-semibold text-rose-600 transition-colors duration-200 hover:bg-rose-50"
            >
              <LogOut className="h-4 w-4" /> Déconnexion
            </button>
          </nav>
        </aside>

        <div className="min-w-0">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
